import * as React from 'react';
import { Group, createStyles } from '@mantine/core';
import { YearPickerInput as YearPickerInputBase, YearPickerInputProps } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        error: commonFieldStyles.error,
        icon: commonFieldStyles.icon,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description
    };
});

export interface CustomYearPickerInputProps extends YearPickerInputProps, GenericFieldProps {
    key?: string;
}

export const YearPickerInput: React.FC<CustomYearPickerInputProps> = ({
    readOnly,
    labelKey,
    label,
    descriptionKey,
    tooltipKey,
    parentform,
    targetperiod,
    highlight,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });

    return (
        <YearPickerInputBase
            classNames={{
                root: classes.root,
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly,
                    [classes.inputHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                error: classes.error,
                icon: cx(classes.icon, {
                    [classes.hiddenDisplay]: readOnly
                }),
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                })
            }}
            label={
                <>
                    <span>{inputLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            icon={<IconCalendar size="1.2rem" stroke={1.5} />}
            popoverProps={{
                withinPortal: true
            }}
            {...props}
        />
    );
};
